// PGP (OpenPGP) key generation via micro-key-producer (pure JS, Ed25519/RFC 4880).
// Runs entirely in the browser; the 32-byte seed comes from the Web Crypto CSPRNG.
// The private key is passphrase-protected by the library.
import getKeys from 'micro-key-producer/pgp.js';

export interface PgpKeyPair {
  keyId: string;
  publicKey: string;
  privateKey: string;
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  // getRandomValues caps at 65536 bytes per call; n is tiny here.
  crypto.getRandomValues(out);
  return out;
}

/**
 * Generates an Ed25519 PGP key pair. `user` is the OpenPGP User ID
 * (e.g. "Alice <alice@example.com>"); `passphrase` encrypts the private key.
 */
export function generatePgpKey(user: string, passphrase: string): PgpKeyPair {
  if (!user.trim()) throw new Error('generatePgpKey: user id is required');
  if (!passphrase) throw new Error('generatePgpKey: passphrase is required');
  const seed = randomBytes(32);
  const createdAt = Math.floor(Date.now() / 1000);
  const { keyId, publicKey, privateKey } = getKeys(seed, user, passphrase, createdAt);
  return { keyId, publicKey, privateKey };
}

/** Builds an OpenPGP User ID from a name and optional email. */
export function buildUserId(name: string, email: string): string {
  const n = name.trim();
  const e = email.trim();
  return e ? `${n} <${e}>` : n;
}
